import type { FC, ReactNode } from 'react';
import type { ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type {
  ListItemProps as PaperListItemProps,
  SurfaceProps,
  TextProps
} from 'react-native-paper';
import { Divider, List, Surface, Text } from 'react-native-paper';

import { RightIcon } from '@/assets';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { SVGComponent } from '@/types';

// TODO: move to theme
const RIGHT_ICON_SIZE = 16;

const styles = StyleSheet.create({
  content: { paddingLeft: 16 },
  title: {
    flex: 1,
    flexGrow: 1,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  text: { flex: 1, flexGrow: 1 },
  badge: { flexShrink: 1, minWidth: 0, maxWidth: '66%' },
  leftIcon: { paddingLeft: 16 }
});

export type ListItemProps = {
  testID?: string;
  style?: PaperListItemProps['style'];
  containerStyle?: PaperListItemProps['containerStyle'];
  contentStyle?: PaperListItemProps['contentStyle'];
  title?: ReactNode;
  overwriteTitleNode?: ReactNode;
  titleVariant?: TextProps<string>['variant'];
  description?: ReactNode;
  descriptionVariant?: TextProps<string>['variant'];
  descriptionColor?: string;
  numberOfLinesDescription?: TextProps<string>['numberOfLines'];
  textOrder?: ViewStyle['flexDirection'];
  badge?: ReactNode;
  color?: string;
  onPress?: PaperListItemProps['onPress'];
  icon?: SVGComponent;
  iconSize?: number;
  iconStyle?: ViewStyle;
  arrowIcon?: SVGComponent;
  hideRightArrow?: boolean;
  hideDivider?: boolean;
  borderRadius?: number;
  /**
   * Use to change the background color of the wrapper.
   * Note: Setting the bg via style leads to no hover color in web
   */
  backgroundColor?: string;
  elevation?: SurfaceProps['elevation'];
};

export const ListItem: FC<ListItemProps> = ({
  testID,
  style,
  containerStyle,
  contentStyle,
  title,
  overwriteTitleNode,
  titleVariant = 'bodyLarge',
  description,
  descriptionVariant = 'labelMedium',
  descriptionColor,
  numberOfLinesDescription = 1,
  textOrder = 'column',
  badge,
  color,
  onPress,
  icon,
  iconSize = 20,
  iconStyle,
  arrowIcon,
  hideRightArrow = false,
  hideDivider = false,
  borderRadius = 0,
  backgroundColor = 'transparent',
  elevation = 0
}) => {
  const { colors } = useAppTheme();
  const itemColor = color ?? colors.onBackground;

  return (
    <Surface
      elevation={elevation}
      style={{
        borderRadius,
        backgroundColor,
        overflow: borderRadius === 0 ? undefined : 'hidden'
      }}
    >
      <List.Item
        // Todo: Workaround for: https://github.com/callstack/react-native-paper/issues/4810#issuecomment-3445337084
        borderless
        testID={testID}
        style={style}
        onPress={onPress}
        contentStyle={[styles.content, contentStyle]}
        containerStyle={containerStyle}
        title={(props) => (
          <View style={styles.title}>
            <View style={[styles.text, { flexDirection: textOrder }]}>
              {description && (
                <Text
                  style={{ color: descriptionColor ?? itemColor }}
                  ellipsizeMode="tail"
                  numberOfLines={numberOfLinesDescription}
                  variant={descriptionVariant}
                >
                  {description}
                </Text>
              )}
              {overwriteTitleNode ?? (
                <Text
                  selectable={props.selectable}
                  ellipsizeMode="tail"
                  numberOfLines={1}
                  variant={titleVariant}
                  style={{ color: itemColor }}
                >
                  {title}
                </Text>
              )}
            </View>
            {badge && (
              <View style={styles.badge}>
                {typeof badge === 'string' || typeof badge === 'number' ? (
                  <Text
                    variant="labelSmall"
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {badge}
                  </Text>
                ) : (
                  badge
                )}
              </View>
            )}
          </View>
        )}
        left={
          icon
            ? (props) => (
                <List.Icon
                  {...props}
                  icon={() =>
                    icon({
                      fill: itemColor,
                      width: iconSize,
                      height: iconSize
                    })
                  }
                  style={[styles.leftIcon, iconStyle]}
                />
              )
            : undefined
        }
        right={
          hideRightArrow
            ? undefined
            : (props) => (
                <List.Icon
                  {...props}
                  icon={() =>
                    arrowIcon ? (
                      arrowIcon({
                        fill: itemColor,
                        width: RIGHT_ICON_SIZE,
                        height: RIGHT_ICON_SIZE
                      })
                    ) : (
                      <RightIcon
                        fill={itemColor}
                        height={RIGHT_ICON_SIZE}
                        width={RIGHT_ICON_SIZE}
                      />
                    )
                  }
                />
              )
        }
      />
      {!hideDivider && <Divider />}
    </Surface>
  );
};
